import React from 'react';
import Reveal from './Reveal';

const INDUSTRIES = [
  { icon: '🏥', title: 'Healthcare & Wellness', desc: 'AI health analytics, telemedicine platforms, wellness tracking, and medical data solutions.', projects: ['FitPulseBot Health SaaS', 'Medical Analytics', 'Wellness Apps'], accent: 'brand-green' },
  { icon: '🎓', title: 'Education & EdTech', desc: 'AI tutoring systems, adaptive learning platforms, and intelligent content management.', projects: ['AI Visual Teacher', 'E-Learning Platforms', 'Skill Assessments'], accent: 'brand-blue' },
  { icon: '🛒', title: 'Retail & E-commerce', desc: 'Personalized shopping, recommendation engines, inventory and customer analytics.', projects: ['E-commerce Platforms', 'Recommendation AI', 'Inventory Systems'], accent: 'brand-gold' },
  { icon: '💰', title: 'Finance & Fintech', desc: 'Payment gateways, trading algorithms, risk assessment, and financial analytics.', projects: ['Payment Gateways', 'Trading Systems', 'Risk Tools'], accent: 'brand-purple' },
  { icon: '🏭', title: 'Manufacturing & SMEs', desc: 'Process automation, supply chain optimization, and predictive maintenance AI.', projects: ['Automation Systems', 'Supply Chain Apps', 'Quality Control'], accent: 'brand-cyan' },
  { icon: '🚀', title: 'Startups & Innovators', desc: 'MVP development, scalable architectures, and rapid prototyping for bold ideas.', projects: ['MVP Development', 'Scalable Systems', 'Rapid Prototyping'], accent: 'brand-pink' },
];

const PORTFOLIO_HIGHLIGHTS = [
  { title: 'FitPulseBot', category: 'Health SaaS', desc: 'Comprehensive health & fitness ecosystem with AI-powered insights and real-time tracking.', tech: ['React', 'Node.js', 'AI/ML', 'Cloud'], status: 'Live', statusColor: 'brand-green' },
  { title: 'AI Visual Teacher', category: 'EdTech Platform', desc: 'Textbook-to-video AI conversion using computer vision and NLP for adaptive learning.', tech: ['Python', 'Computer Vision', 'NLP', 'Video AI'], status: 'Development', statusColor: 'brand-blue' },
  { title: 'Enterprise Web Apps', category: 'Enterprise Solutions', desc: 'Multiple B2B/B2C applications across industries with microservices architecture.', tech: ['Full-Stack', 'Cloud', 'Microservices', 'APIs'], status: 'Delivered', statusColor: 'brand-cyan' },
  { title: 'AI Marketing SaaS', category: 'Marketing Automation', desc: 'Intelligent marketing automation with predictive analytics and content AI.', tech: ['AI/ML', 'Analytics', 'Automation', 'SaaS'], status: 'Prototype', statusColor: 'brand-gold' },
];

const Portfolio = () => (
  <section id="portfolio" className="section-padding bg-brand-bg relative overflow-hidden scroll-mt-16">
    <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-cyan/5 blur-[130px] pointer-events-none" />

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

      <Reveal from="up" className="section-header">
        <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm font-medium text-brand-cyan border border-brand-cyan/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan" />
          Industries & Work
        </div>
        <h2 className="text-4xl md:text-6xl font-black text-brand-text mb-5">
          Industries We <span className="text-gradient">Serve</span>
        </h2>
        <p className="text-lg text-brand-muted max-w-2xl mx-auto">
          Delivering intelligent solutions across diverse sectors — transforming how industries work with AI and modern engineering.
        </p>
      </Reveal>

      {/* Industries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={i} from="up" delay={i * 70}>
            <div className={`card group hover:shadow-glow-blue hover:border-${ind.accent}/30 transition-all duration-300 h-full`}>
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{ind.icon}</div>
              <h3 className={`text-brand-text font-bold text-lg mb-2 group-hover:text-${ind.accent} transition-colors`}>{ind.title}</h3>
              <p className="text-brand-muted text-sm leading-relaxed mb-4">{ind.desc}</p>
              <div className="space-y-1.5">
                {ind.projects.map((p, pi) => (
                  <div key={pi} className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full bg-${ind.accent} flex-shrink-0`} />
                    <span className="text-brand-dim text-xs">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Portfolio Highlights */}
      <Reveal from="up">
        <div className="glass rounded-2xl p-8 border border-brand-border mb-10">
          <h3 className="text-2xl font-bold text-brand-text mb-8 text-center">Portfolio Highlights</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PORTFOLIO_HIGHLIGHTS.map((p, i) => (
              <div key={i} className="bg-brand-card rounded-xl p-6 border border-brand-border hover:border-brand-blue/30 transition-all duration-300 group">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-brand-text font-bold">{p.title}</h4>
                    <p className={`text-${p.statusColor} text-xs font-semibold mt-0.5`}>{p.category}</p>
                  </div>
                  <span className={`tag bg-${p.statusColor}/15 text-${p.statusColor} border border-${p.statusColor}/25`}>{p.status}</span>
                </div>
                <p className="text-brand-muted text-sm leading-relaxed mb-4">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.tech.map((t, ti) => (
                    <span key={ti} className="text-xs px-2.5 py-1 rounded-full bg-brand-border text-brand-muted border border-brand-border">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Engagement Models */}
      <Reveal from="up">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: '📦', label: 'SaaS Subscriptions', sub: 'Products like FitPulseBot', color: 'brand-green' },
            { icon: '🔧', label: 'Custom Projects', sub: 'End-to-end project delivery', color: 'brand-blue' },
            { icon: '👩‍💻', label: 'Staff Augmentation', sub: 'Freelance & outsourcing', color: 'brand-gold' },
            { icon: '🎓', label: 'Training Programs', sub: 'Corporate skill development', color: 'brand-purple' },
          ].map((m, i) => (
            <div key={i} className={`card text-center hover:border-${m.color}/30 transition-all duration-300 group`}>
              <div className={`text-3xl mb-3 group-hover:scale-110 transition-transform`}>{m.icon}</div>
              <div className="text-brand-text font-semibold text-sm mb-1">{m.label}</div>
              <div className="text-brand-dim text-xs">{m.sub}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default Portfolio;
